import React from "react"

import { useTranslation } from "react-i18next"
import { useMutation, useQueryClient } from "react-query"
import { useLocation, useNavigate } from "react-router-dom"

import { Dialog, DialogActions, DialogContent, DialogTitle, useMediaQuery, useTheme } from "@mui/material"
import { Button, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../../services/KonanAPI"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelListCreate } from "../../../types/generated/api/ModelListCreate"

type Props = {
  open: boolean
  onClose: () => void
  projectId: string
  trainingUUID: string
  trainingOrRetraining: string
  liveModel: Model | ModelListCreate | undefined
}

export function AbortTrainingJobDialog(props: Readonly<Props>): React.ReactElement {
  const { open, onClose, projectId, trainingUUID, trainingOrRetraining, liveModel } = props
  const { t } = useTranslation("TrainingJob")
  const theme = useTheme()

  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  const abortTrainingJobMutation = useMutation<AxiosResponse, AxiosError>(() =>
    KonanAPI.deleteTrainingModel(projectId, trainingUUID),
  )

  const abortRetrainingJobMutation = useMutation<AxiosResponse, AxiosError>(() =>
    KonanAPI.deleteRetrainingModel(projectId),
  )

  const handleAbort = async (): Promise<void> => {
    try {
      if (trainingOrRetraining === "training") {
        await abortTrainingJobMutation.mutateAsync()
        await queryClient.invalidateQueries(["training-jobs", projectId])
        onClose()
        // Invalidate react-query queries
      } else if (trainingOrRetraining === "retraining") {
        await abortRetrainingJobMutation.mutateAsync()
        await queryClient.invalidateQueries(["retraining-jobs", liveModel?.uuid])
        onClose()
      }

      navigate({
        pathname: location.pathname,
        search: `tab=failed`,
      })
      NotificationUtils.toast(t("notifications.trainingJobAborted"), { severity: "success" })
    } catch {
      NotificationUtils.toast(t("notifications.abortError"), {
        severity: "error",
      })
      onClose()
    }
  }
  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          width: fullScreen ? "100%" : "400px",
        },
      }}
      onClose={(_, reason) => {
        if ((reason === "backdropClick" || reason === "escapeKeyDown") && abortRetrainingJobMutation?.isLoading) {
          return undefined
        } else {
          onClose()
        }
      }}
    >
      <DialogTitle className="dialog-header-base">
        <Typography variant="h2-bold">{t("dialogs.abort.title")}</Typography>
      </DialogTitle>

      <DialogContent className="dialog-content-base">
        <Typography variant="h3-regular" gutterBottom>
          {t("dialogs.abort.message")}
        </Typography>

        <Typography variant="p">Are you sure you want to abort it?</Typography>
      </DialogContent>

      <DialogActions className="dialog-actions-base">
        <Button onClick={() => onClose()} variant={"secondary"}>
          {t("dialogs.abort.cancel")}
        </Button>
        <Button
          onClick={() => handleAbort()}
          disabled={abortRetrainingJobMutation.isLoading || abortTrainingJobMutation.isLoading}
          variant="dangerous"
          isLoading={abortRetrainingJobMutation?.isLoading || abortTrainingJobMutation.isLoading}
        >
          Abort
        </Button>
      </DialogActions>
    </Dialog>
  )
}
